import { Base } from '@studiometa/js-toolkit';
import { getClosestParent } from '@studiometa/js-toolkit';
import mapboxgl from 'mapbox-gl';
import MapComponent from './Map.js';

/**
 * Parse a coordinate value, accepting either a decimal number or a
 * DMS string like `48°37'14.02"N` / `7°42'03.40"E`.
 * @param {string|number} value
 * @returns {number} decimal degrees, or NaN if unparseable
 */
function parseCoordinate(value) {
  if (value === null || value === undefined || value === '') {
    return Number.NaN;
  }
  const direct = Number(value);
  if (Number.isFinite(direct)) {
    return direct;
  }
  const str = String(value).trim().replace(',', '.');
  const match = str.match(
    /^(-?\d+(?:\.\d+)?)\s*°\s*(?:(\d+(?:\.\d+)?)\s*['′])?\s*(?:(\d+(?:\.\d+)?)\s*["″])?\s*([NSEWnsew])?$/,
  );
  if (!match) {
    return Number.NaN;
  }
  const [, deg, min, sec, hemi] = match;
  const decimal =
    Math.abs(Number(deg)) + (min ? Number(min) / 60 : 0) + (sec ? Number(sec) / 3600 : 0);
  const isNegative = Number(deg) < 0 || (hemi && /[SWsw]/.test(hemi));
  return isNegative ? -decimal : decimal;
}

/**
 * @typedef {{
 *   id: number;
 *   latitude: number;
 *   longitude: number;
 *   title: string;
 *   location: string;
 *   dates: string;
 *   image: string;
 *   category: string;
 *   link: string;
 * }} ProjectItem
 */

/**
 * @typedef {{
 *   $options: {
 *     source: ProjectItem[];
 *     projectId: number;
 *   };
 * }} MapboxClusterProps
 */

/**
 * MapboxCluster component for displaying projects on the map
 * @augments {Base<MapboxClusterProps>}
 */
export default class MapboxCluster extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'MapboxCluster',
    options: {
      source: Array,
      projectId: Number,
    },
  };

  /** @type {object} */
  map;

  /** @type {number|null} */
  selectedFeatureId = null;

  /** @type {number|null} */
  hoveredFeatureId = null;

  /** @type {Map<number, mapboxgl.Marker>} */
  markers = new Map();

  /** @type {mapboxgl.Popup|null} */
  popup = null;

  /** @type {number|null} */
  mapLoadedTimeoutId = null;

  /**
   * Get closest Map parent
   * @returns {MapComponent}
   */
  get mapComponent() {
    return getClosestParent(this, MapComponent);
  }

  /**
   * Get GeoJSON feature collection
   * @returns {object}
   */
  get geoJson() {
    return {
      type: 'FeatureCollection',
      features: this.$options.source
        .filter(
          (item) =>
            Number.isFinite(parseCoordinate(item.latitude)) &&
            Number.isFinite(parseCoordinate(item.longitude)),
        )
        .map((item) => this.itemToGeoJsonFeature(item)),
    };
  }

  /**
   * mounted
   */
  async mounted() {
    this.injectStyles();

    const mapComponent = this.mapComponent;

    if (!mapComponent) {
      console.error('Map parent component not found');
      return;
    }

    // Poll for map to be loaded, until the component is destroyed
    const checkMapLoaded = () => {
      this.mapLoadedTimeoutId = null;

      if (!this.$isMounted) {
        return;
      }

      if (mapComponent.map && mapComponent.map.isStyleLoaded && mapComponent.map.isStyleLoaded()) {
        this.onMapReady();
      } else {
        this.mapLoadedTimeoutId = setTimeout(checkMapLoaded, 100);
      }
    };

    checkMapLoaded();
  }

  /**
   * Inject popup and marker styles
   */
  injectStyles() {
    if (document.querySelector('#mapbox-popup-styles')) {
      return;
    }
    const style = document.createElement('style');
    style.id = 'mapbox-popup-styles';
    style.textContent = `
      .mapboxgl-popup {
        z-index: 1000 !important;
      }
      .mapboxgl-popup-content {
        padding: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
      }
      .mapboxgl-popup-content img {
        transition: transform 300ms ease-in-out;
      }
      .mapboxgl-popup-content:hover img {
        transform: scale(1.1);
      }
      .mapboxgl-popup-tip {
        display: none !important;
      }
      .mapboxgl-popup-close-button {
        display: none;
      }
      .map-marker {
        display: block;
      }
      .map-marker__dot {
        appearance: none;
        box-sizing: border-box;
        display: block;
        width: 18px;
        height: 18px;
        padding: 0;
        margin: 0;
        border: 2px solid rgba(168, 154, 156, 0.3);
        border-radius: 9999px;
        background: #1B1B1B;
        cursor: pointer;
        transform-origin: center;
        transition: transform 300ms ease-in-out, background-color 300ms ease-in-out, border-color 300ms ease-in-out, opacity 300ms ease-in-out;
      }
      .map-marker.is-selected .map-marker__dot {
        background: #b23446;
        border-color: rgba(178, 52, 70, 0.3);
      }
      .map-marker.is-hover .map-marker__dot {
        background: #b23446;
        border-color: rgba(178, 52, 70, 0.3);
        transform: scale(1.1);
      }
      .map-marker.is-dimmed .map-marker__dot {
        opacity: 0.6;
      }
    `;
    document.head.append(style);
  }

  /**
   * onMapReady
   */
  onMapReady() {
    this.map = this.mapComponent.map;
    this.addSourceData();
    this.addClusterLayer();
    this.addClusterCountLayer();
    this.createMarkers();
    this.onClusterClick();

    this.map.on('mouseenter', 'clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', 'clusters', () => {
      this.map.getCanvas().style.cursor = '';
    });

    this.map.on('moveend', () => this.updateMarkerVisibility());
    this.map.on('zoomend', () => this.updateMarkerVisibility());
    this.map.on('sourcedata', (e) => {
      if (e.sourceId === 'projects' && e.isSourceLoaded) {
        this.updateMarkerVisibility();
      }
    });

    if (this.$options.projectId) {
      this.autoOpenPopup(Number(this.$options.projectId));
    }
  }

  /**
   * Add Source Data to map
   */
  addSourceData() {
    this.map.addSource('projects', {
      type: 'geojson',
      data: this.geoJson,
      cluster: true,
      clusterMaxZoom: 8,
      clusterRadius: 50,
    });
  }

  /**
   * addClusterLayer
   */
  addClusterLayer() {
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'projects',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#1B1B1B',
        'circle-radius': 12,
        'circle-stroke-width': 2,
        'circle-stroke-color': 'rgba(168, 154, 156, 0.3)',
      },
    });
  }

  /**
   * addClusterCountLayer
   */
  addClusterCountLayer() {
    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'projects',
      filter: ['has', 'point_count'],
      paint: {
        'text-color': '#ffffff',
      },
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-size': 16,
      },
    });
  }

  /**
   * Create a DOM marker for each item in the source
   */
  createMarkers() {
    this.$options.source.forEach((item) => {
      const lng = parseCoordinate(item.longitude);
      const lat = parseCoordinate(item.latitude);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
        return;
      }
      const el = this.createMarkerElement(item);
      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(this.map);
      this.markers.set(item.id, marker);
    });
  }

  /**
   * Build a marker DOM element with hover/click handlers
   * @param {ProjectItem} item
   * @returns {HTMLDivElement}
   */
  createMarkerElement(item) {
    const wrapper = document.createElement('div');
    wrapper.className = 'map-marker';
    wrapper.dataset.featureId = String(item.id);

    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'map-marker__dot';
    dot.setAttribute('aria-label', item.title || 'Marker');

    dot.addEventListener('mouseenter', () => this.onMarkerEnter(item.id));
    dot.addEventListener('mouseleave', () => this.onMarkerLeave());
    dot.addEventListener('click', (event) => {
      event.stopPropagation();
      const feature = this.geoJson.features.find((feat) => feat.id === item.id);
      if (feature) {
        this.openPopupForFeature(feature);
      }
    });

    wrapper.append(dot);
    return wrapper;
  }

  /**
   * Hide markers that are currently inside a cluster
   */
  updateMarkerVisibility() {
    if (!this.map.getSource('projects')) {
      return;
    }

    const features = this.map.querySourceFeatures('projects');
    const visibleIds = new Set();
    for (const feature of features) {
      if (!feature.properties.cluster) {
        visibleIds.add(feature.id);
      }
    }

    for (const [id, marker] of this.markers) {
      const el = marker.getElement();
      el.style.display = visibleIds.has(id) ? '' : 'none';
    }
  }

  /**
   * Apply hover state on a marker and dim all others
   * @param {number} featureId
   */
  onMarkerEnter(featureId) {
    this.hoveredFeatureId = featureId;
    for (const [id, marker] of this.markers) {
      const el = marker.getElement();
      if (id === featureId) {
        el.classList.add('is-hover');
        el.classList.remove('is-dimmed');
      } else {
        el.classList.add('is-dimmed');
        el.classList.remove('is-hover');
      }
    }
  }

  /**
   * Clear hover and dim states from all markers
   */
  onMarkerLeave() {
    this.hoveredFeatureId = null;
    for (const marker of this.markers.values()) {
      const el = marker.getElement();
      el.classList.remove('is-hover', 'is-dimmed');
    }
  }

  /**
   * onClusterClick
   */
  onClusterClick() {
    this.map.on('click', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters'],
      });
      const clusterId = features[0].properties.cluster_id;
      this.map.getSource('projects').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;

        this.map.easeTo({
          center: features[0].geometry.coordinates,
          zoom,
        });
      });
    });
  }

  /**
   * Close the currently open popup, if any
   */
  closePopup() {
    if (!this.popup) {
      return;
    }

    // The `close` event handler resets `this.popup` and the selected state
    this.popup.remove();
  }

  /**
   * Open popup for a specific feature
   * @param {object} feature
   */
  openPopupForFeature(feature) {
    const { title, location, dates, image, category, link } = feature.properties;
    const coordinates = feature.geometry.coordinates.slice();

    // Close the popup which is currently open, if any
    this.closePopup();

    // Reset previous selection
    if (this.selectedFeatureId !== null) {
      const prev = this.markers.get(this.selectedFeatureId);
      if (prev) {
        prev.getElement().classList.remove('is-selected');
      }
    }

    // Set new selection
    this.selectedFeatureId = feature.id;
    const current = this.markers.get(feature.id);
    if (current) {
      current.getElement().classList.add('is-selected');
    }

    if (['mercator', 'equirectangular'].includes(this.map.getProjection().name)) {
      while (Math.abs(this.map.getCenter().lng - coordinates[0]) > 180) {
        coordinates[0] += this.map.getCenter().lng > coordinates[0] ? 360 : -360;
      }
    }

    const popupHTML = `
      <a href="${link || '#'}" style="display: block; width: 320px; background: white; overflow: hidden; text-decoration: none; color: inherit;">
        ${image ? `<img src="${image}" alt="${title}" style="width: 100%; height: 200px; object-fit: cover; display: block;">` : ''}
        <div style="padding: 12px 16px 16px 16px; position: relative; z-index: 1; background:#FFFFFF;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
            <h3 style="font-weight: 300; font-size: 1rem; margin: 0; line-height: 1.4; color: #000; flex: 1;">${title}</h3>
            ${category ? `<p style="font-size: 0.75rem; color: #C14444; text-transform: uppercase; margin: 0 0 0 16px; line-height: 1.4; letter-spacing: 0.5px; text-align: right;">${category}</p>` : ''}
          </div>
          <div style="display: flex; justify-content: space-between;">
            <p style="font-size: 0.75rem; color: #C14444; text-transform: uppercase; margin: 0; line-height: 1.4; letter-spacing: 0.5px;">${location}</p>
            ${dates ? `<p style="font-size: 0.75rem; color: #C14444; text-transform: uppercase; margin: 0; line-height: 1.4; letter-spacing: 0.5px; text-align: right;">${dates}</p>` : ''}
          </div>
        </div>
      </a>
    `;

    try {
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: true,
        offset: [0, -12],
        maxWidth: 'none',
      })
        .setLngLat(coordinates)
        .setHTML(popupHTML);

      // Reset selection when popup closes
      popup.on('close', () => {
        if (this.popup !== popup) {
          return;
        }

        this.popup = null;

        if (this.selectedFeatureId !== null) {
          const marker = this.markers.get(this.selectedFeatureId);
          if (marker) {
            marker.getElement().classList.remove('is-selected');
          }
          this.selectedFeatureId = null;
        }
      });

      this.popup = popup;
      popup.addTo(this.map);
    } catch (error) {
      console.error('Error creating popup:', error);
    }
  }

  /**
   * Auto-open popup for a project by ID
   * @param {number} projectId
   */
  autoOpenPopup(projectId) {
    // Find the feature in the GeoJSON data
    const feature = this.geoJson.features.find((feat) => {
      return feat.id === projectId;
    });

    if (!feature) {
      return;
    }

    this.mapComponent.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const coordinates = feature.geometry.coordinates;

    this.map.easeTo({
      center: coordinates,
      zoom: 12,
    });

    // Open popup after map animation completes
    this.map.once('idle', () => {
      this.openPopupForFeature(feature);
    });
  }

  /**
   * Transform a project into a valid GeoJSON feature
   * @param {object} item
   * @param {number} item.id
   * @param {number} item.latitude
   * @param {number} item.longitude
   * @param {string} item.title
   * @param {string} item.location
   * @param {string} item.dates
   * @param {string} item.image
   * @param {string} item.category
   * @param {string} item.link
   * @returns {object}
   */
  itemToGeoJsonFeature(item) {
    const { id, latitude, longitude, title, location, dates, image, category, link } = item;

    return {
      id,
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [parseCoordinate(longitude), parseCoordinate(latitude)],
      },
      properties: {
        title: title || '',
        location: location || '',
        dates: dates || '',
        image: image || '',
        category: category || '',
        link: link || '',
      },
    };
  }

  /**
   * destroyed
   */
  destroyed() {
    if (this.mapLoadedTimeoutId !== null) {
      clearTimeout(this.mapLoadedTimeoutId);
      this.mapLoadedTimeoutId = null;
    }

    this.closePopup();

    for (const marker of this.markers.values()) {
      marker.remove();
    }
    this.markers.clear();
  }
}
